// ReadthroughView: readthrough recording mode (React version)

import { useState, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../store';
import { setResumeIndex } from '../store/practiceSlice';
import { getWorkingLines } from './OptionsMenu';
import { getWorkingCharacterSet } from '../lib/persistence';
import { saveAudioBlob, getAudioBlob, deleteAudioBlob, createAudioId } from '../lib/audio-storage';
import { syncRecordedAudioFromWorkingLines, persistLastMode } from '../lib/persistence';
import { ensureSessionMicStream } from '../services/mic-stt';
import { stopSpeech } from '../services/speech';
import type { ViewName } from '../app';

interface ReadthroughViewProps {
    onNavigate: (view: ViewName) => void;
}

export function ReadthroughView({ onNavigate }: ReadthroughViewProps) {
    const dispatch = useAppDispatch();
    const myCharacters = useAppSelector(s => s.script.myCharacters);
    const { removeStageDirections } = useAppSelector(s => s.settings);

    const [myRole, setMyRole] = useState<Set<string>>(new Set());
    const [readthroughIndex, setReadthroughIndex] = useState(0);
    const [isRecording, setIsRecording] = useState(false);
    const [pendingAudioId, setPendingAudioId] = useState<string | null>(null);
    const [showReviewRow, setShowReviewRow] = useState(false);
    const [statusText, setStatusText] = useState('');

    const recorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const workingLines = getWorkingLines();
    const characters = Array.from(getWorkingCharacterSet());

    // Build line indices (lines not belonging to my role)
    const readthroughLineIndices = workingLines
        .map((line, i) => ({ line, i }))
        .filter(({ line, i }) => {
            if (line.isStageDirection) return !removeStageDirections;
            return !myRole.has(line.character);
        })
        .map(({ i }) => i);

    const isDone = readthroughIndex >= readthroughLineIndices.length;
    const currentLineIdx = readthroughLineIndices[readthroughIndex];
    const currentLine = currentLineIdx !== undefined ? workingLines[currentLineIdx] : null;

    function toggleRole(role: string) {
        setMyRole(prev => {
            const next = new Set(prev);
            if (next.has(role)) next.delete(role);
            else next.add(role);
            return next;
        });
        setReadthroughIndex(0);
        setPendingAudioId(null);
        setShowReviewRow(false);
    }

    async function startRecording() {
        const stream = await ensureSessionMicStream();
        if (!stream) { setStatusText('Microphone access denied.'); return; }
        streamRef.current = stream;

        const recorder = new MediaRecorder(stream);
        recorderRef.current = recorder;
        setIsRecording(true);
        setStatusText('Recording...');

        const chunks: Blob[] = [];
        recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
        recorder.onstop = async () => {
            const blob = new Blob(chunks, { type: 'audio/webm' });
            const audioId = createAudioId();
            await saveAudioBlob(audioId, blob).catch(console.error);

            if (workingLines[currentLineIdx]) {
                workingLines[currentLineIdx].recordedAudioId = audioId;
            }
            syncRecordedAudioFromWorkingLines();

            setIsRecording(false);
            setPendingAudioId(audioId);
            setShowReviewRow(true);
            setStatusText('Recorded! Play it back or keep it.');
        };
        recorder.start();
    }

    function stopRecording() {
        if (recorderRef.current?.state !== 'inactive') {
            try { recorderRef.current?.stop(); } catch {}
        }
    }

    async function playback() {
        if (!pendingAudioId) return;
        const blob = await getAudioBlob(pendingAudioId);
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => URL.revokeObjectURL(url);
        audio.play();
    }

    function rerecord() {
        if (pendingAudioId && workingLines[currentLineIdx]) {
            delete workingLines[currentLineIdx].recordedAudioId;
            syncRecordedAudioFromWorkingLines();
        }
        setPendingAudioId(null);
        setShowReviewRow(false);
        setStatusText('');
    }

    function keepAndNext() {
        setPendingAudioId(null);
        setShowReviewRow(false);
        setReadthroughIndex(i => i + 1);
        setStatusText('');
    }

    function skip() {
        if (isRecording) return;
        setPendingAudioId(null);
        setReadthroughIndex(i => i + 1);
    }

    return (
        <div className="character-select-container" id="readthrough-view">
            <h1 className="upload-title">Record Read-Through</h1>
            <p className="upload-subtitle">Record each line in order. These recordings will play back during practice.</p>

            <div style={{ textAlign: 'left', width: '100%', marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '6px' }}>My Role(s)</label>
                <div className="character-list" style={{ margin: '0 0 6px 0' }}>
                    {characters.map(char => (
                        <div
                            key={char}
                            className={`character-option ${myRole.has(char) ? 'selected' : ''}`}
                            style={{ padding: '10px 14px' }}
                            onClick={() => toggleRole(char)}
                        >
                            <div className="character-checkbox"></div>
                            <div className="character-name">{char}</div>
                        </div>
                    ))}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '6px' }}>
                    {myRole.size > 0
                        ? `Recording all roles except ${Array.from(myRole).join(', ')}`
                        : 'Select your role(s) to start recording'}
                </div>
            </div>

            <div style={{ border: '1px solid #eee', borderRadius: '8px', padding: '16px', textAlign: 'left' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                    {isDone ? 'DONE' : (myRole.size === 0 ? '-' : (currentLine?.character || 'STAGE'))}
                </div>
                <div style={{ fontSize: '1.1rem' }}>
                    {isDone ? 'All lines recorded.' : (myRole.size === 0 ? 'Select your role(s) to begin' : (currentLine?.dialogue || ''))}
                </div>
            </div>

            {!showReviewRow && !isDone && (
                <div className="btn-row">
                    <button
                        className={`btn btn-primary record-btn ${isRecording ? 'recording' : ''}`}
                        onClick={() => { if (myRole.size === 0) { alert('Please select your role first.'); return; } isRecording ? stopRecording() : startRecording(); }}
                    >
                        {isRecording ? '⏹️ Stop Recording' : '🎙️ Record Line'}
                    </button>
                    <button className="btn btn-secondary" onClick={skip}>Skip</button>
                    <button className="btn btn-secondary" onClick={() => { if (!isRecording) onNavigate('practice'); }}>Done</button>
                </div>
            )}

            {showReviewRow && (
                <div className="btn-row">
                    <button className="btn btn-secondary" onClick={playback}>▶ Play Back</button>
                    <button className="btn btn-secondary" onClick={rerecord}>↺ Re-record</button>
                    <button className="btn btn-primary" onClick={keepAndNext}>✓ Keep</button>
                </div>
            )}

            {isDone && (
                <div className="btn-row">
                    <button className="btn btn-primary" onClick={() => {/* TODO: full run-through */}}>▶ Play Full Run-Through</button>
                </div>
            )}

            <div className="record-status">{statusText}</div>
        </div>
    );
}
